import * as PIXI from 'pixi.js';
import { Scene } from "pixi-scenegraph";
import game from '@/model/game';
import { loadChart } from '@/loader/mxl';
import { activateScene, resetGameScene, getCurrentSceneName } from '@/scenes';
import { EVENT_BUS } from '@/model/event-bus';
import { TonejsAudio, Fx } from '@/audio/audio';
import { mapNoteDebug, mapKey } from '@/util/font-util';
import { loadMidi } from '@/loader/mxl';
import { showPausePopup, closePausePopup } from '@/ui/dialog'
export class GameSceneAbstract extends Scene {
    constructor(name) {
        super(name);
        this.sceneName = name;
        this.ticker = new PIXI.Ticker();
    }

    onReveal() {
        this.ui.onReveal();
    }

    async setPayload(chart) {
        game.isPlaying = false;
        if (!chart || !chart.mxlUrl) {
            throw new Error(`Game.chart has to be defined \n${JSON.stringify(chart || '')}`);
        }
        this.chart = chart;
        await this.loadMxl(chart.mxlUrl);
        this.startGame();
    }

    startListeningToGameEvents() {
        EVENT_BUS.on('NOTE_HIT', (noteHit) => {
            const note = this.notesToUpdate.find(
                (wrapped) => wrapped.xmlNote.id === noteHit.id);
            this.ui.animateNoteHitAndMiss(note, true);
        });
        EVENT_BUS.on('NOTE_MISS', (noteHit) => {
            const note = this.notesToUpdate.find(
                (wrapped) => wrapped.xmlNote.id === noteHit.id);
            this.ui.animateNoteHitAndMiss(note, false);
        });
        EVENT_BUS.on('GAME_FAIL', () => {
            this.showFailScreen();
        });
        EVENT_BUS.on('MENU_ENTER', (sceneName) => {
            this.togglePause(sceneName);
        });
        EVENT_BUS.on('MENU_ESC', (sceneName) => {
            this.togglePause(sceneName);
        });
        EVENT_BUS.on('NOTE_STOP_MODE_PAUSE', (note) => {
            this.pauseGame();
        });
        EVENT_BUS.on('NOTE_STOP_MODE_RESUME', ()=>{
            this.resumeGame();
        } )
    }

    togglePause(sceneName) {
        console.debug('togglePause game.isPlaying = ', game.isPlaying, sceneName);
        if (getCurrentSceneName() === sceneName) {
            if (game.isPlaying) {
                showPausePopup();
                this.pauseGame();
            } else {
                closePausePopup();
                this.resumeGame();
            }
        }
    }

    stopAndCleanUp() {
        this.ticker.remove(this.updateFn);
        this.ticker.remove(this.gameUpdateFn);
        PIXI.Ticker.shared.remove(this.backgroundUpdate)
        this.audio.stop();
        resetGameScene();
    }

    showResultsScreen() {
        this.stopAndCleanUp();
        activateScene('results', {
            ...this.chart,
            noteHits: game.noteHits,
            comboStreak: game.comboStreak,
            maxCombo: game.maxCombo,
            newProgress: `${game.totalNotesHit / (game.totalNotesHit + game.totalNotesMissed) * 100}`
        });
    }

    showFailScreen() {
        this.stopAndCleanUp();
        activateScene('fail', {
            ...this.chart,
            newProgress: `${game.totalNotesHit / (game.totalNotesHit + game.totalNotesMissed) * 100}`
        });
    }

    async loadMxl(pathToFile) {
        let loadChartPromise = Promise.resolve();
        if (pathToFile.includes('.mxl')) {
            loadChartPromise = loadChart(pathToFile);
        } else if (pathToFile.includes('.mid')) {
            loadChartPromise = loadMidi(pathToFile)
        } else {
            throw new Error(`Can't load ${pathToFile}, only *.mid and *.mxl files are supported at the moment.\n *.bms is on the roadmap..`)
        }
        return loadChartPromise.then(async ({
                measures, attributes,
                beats, beatType,
                divisionsPerQuarterNote,
                key, notes, meta, boundaries }) => {

                game.beats = beats;
                game.notes = notes;
                this.BEAT_TYPE = beatType;
                this.BEATS = beats;

                if (meta?.audio) {
                    this.audio = meta.audio;
                } else {
                    this.audio = new TonejsAudio(notes, {
                        key: mapKey(key),
                        bpm: game.bpm,
                        beats: beats
                    });
                }

                await this.audio.load();
                if (game.practiceMode && game.practiceModeTempoPercent) {
                    if (typeof this.audio.setSpeed === 'function') {
                        this.audio.setSpeed(game.practiceModeTempoPercent);
                    }
                }
                return {
                    measures, attributes,
                    beats, beatType,
                    divisionsPerQuarterNote,
                    key, notes, meta, boundaries
                }
            });
    }

    startGame() {
        if (!game.isPlaying) {
            game.audio = this.audio;
            game.isPlaying = true;
            this.startListeningToGameEvents();
            this.gameUpdateFn = () => {
                game.update();
                this.ui.update();
            };
            this.updateFn = () => this.update();
            this.ticker.add(this.updateFn);
            this.ticker.add(this.gameUpdateFn);
            this.ticker.start();
            this.audio.play();
            PIXI.Ticker.shared.add(this.backgroundUpdate)
            // this.ui.countdown().then(() => {

            // });
        }
    }

    pauseGame() {
        if (!game.isPlaying ) {
            return;
        }
        game.isPlaying = false;
        this.ticker.stop();
        this.audio.stop();
    }

    resumeGame() {
        if ( game.isPlaying ) {
            return;
        }
        game.isPlaying = true;
        this.ticker.start();
        this.audio.resume();
    }

    backgroundUpdate() {
    }
}